import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import OTApprovalModal from "./OTApprovalModal";

const OTApprovalConfigList = () => {
  const [configs, setConfigs] = useState([]);
  const [activeTab, setActiveTab] = useState("Office"); // Default selection: Office
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      console.log("Calling API:", API_ENDPOINTS.otApprovalConfigs);
      const res = await axiosClient.get(API_ENDPOINTS.otApprovalConfigs);
      console.log("Data received from API:", res.data);
      setConfigs(res.data || []);
    } catch (error) {
      console.error("❌ Error loading configurations:", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this approval step?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.otApprovalConfigs}/${id}`);
      fetchConfigs();
    } catch (error) {
      alert("Delete failed!");
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingConfig && editingConfig.id) {
        await axiosClient.put(`${API_ENDPOINTS.otApprovalConfigs}/${editingConfig.id}`, data);
      } else {
        await axiosClient.post(API_ENDPOINTS.otApprovalConfigs, data);
      }
      fetchConfigs();
      setShowModal(false);
      setEditingConfig(null);
    } catch (error) {
      alert("Error saving data! Please check your connection or data format.");
    }
  };

  // Filter data based on tabs: Office or Tradesman
  const filteredData = configs
    .filter((c) => c.configGroup === activeTab)
    .sort((a, b) => a.stepOrder - b.stepOrder);

  const thStyle = { 
    backgroundColor: "#f8f9fc", 
    color: "#4e73df", 
    fontWeight: "bold", 
    fontSize: "0.8rem", 
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="m-0 font-weight-bold text-dark">OVERTIME APPROVAL WORKFLOW</h5>
              <p className="text-muted small mb-0">Manage multi-stage approval sequences for different personnel categories</p>
            </div>
            <button
              className="btn btn-sm btn-primary px-3 rounded-pill shadow-sm fw-bold"
              onClick={() => {
                setEditingConfig({ 
                  configGroup: activeTab, 
                  stepOrder: filteredData.length + 1,
                  isCCOnly: false 
                });
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-1"></i> Add {activeTab} Step
            </button>
          </div>

          <ul className="nav nav-tabs border-bottom-0">
            {["Office", "Tradesman"].map((tab) => (
              <li className="nav-item" key={tab}>
                <button
                  className={`nav-link fw-bold border-0 ${activeTab === tab ? "active border-bottom border-primary border-3 text-primary" : "text-muted"}`}
                  onClick={() => setActiveTab(tab)}
                  style={{ background: "none", padding: "10px 25px" }}
                >
                  <i className={`fas ${tab === "Office" ? "fa-building" : "fa-tools"} me-2`}></i>
                  {tab === "Office" ? "Office Staff" : "Tradesman (AU)"}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={thStyle}>Sequence</th>
                  <th style={thStyle}>Approver Information</th>
                  <th style={thStyle}>Role / Instructions</th>
                  <th className="text-center" style={thStyle}>Step Type</th>
                  <th className="text-center" style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((c) => (
                    <tr key={c.id}>
                      <td className="text-center">
                        <span className="badge rounded-circle bg-primary text-white" style={{ width: "28px", height: "28px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          {c.stepOrder}
                        </span>
                      </td>
                      <td>
                        <div className="fw-bold text-dark">
                          {c.approver?.fullName || `ID: ${c.approverId || "N/A"}`}
                        </div>
                        <div className="small text-muted">
                           <i className="far fa-envelope me-1"></i>
                           {c.approver?.email || "No email available"}
                        </div>
                      </td>
                      <td>
                        <div className="text-dark small" style={{ maxWidth: "250px" }}>
                            {c.note || <span className="text-muted fst-italic">No specific instructions</span>}
                        </div>
                      </td>
                      <td className="text-center">
                        {c.isCCOnly ? (
                          <span className="badge bg-light text-warning border border-warning px-3">
                            <i className="fas fa-info-circle me-1"></i> CC Only
                          </span>
                        ) : (
                          <span className="badge bg-light text-success border border-success px-3">
                            <i className="fas fa-check-circle me-1"></i> Authorized
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm btn-light text-primary border rounded-circle shadow-sm"
                            title="Modify Step"
                            onClick={() => {
                              setEditingConfig(c);
                              setShowModal(true);
                            }}
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-light text-danger border rounded-circle shadow-sm"
                            title="Remove Step"
                            onClick={() => handleDelete(c.id)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <div className="text-muted">
                        <i className="fas fa-sitemap fa-3x mb-3 opacity-25"></i>
                        <p className="mb-0">No configuration steps found for the <strong>{activeTab}</strong> workflow category.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white border-0 py-3">
            <small className="text-muted fst-italic">
                * Workflow logic: Approval steps are processed in ascending order. "CC Only" steps provide visibility without requiring a signature.
            </small>
        </div>
      </div>

      {showModal && (
        <OTApprovalModal
          show={showModal}
          initialData={editingConfig}
          onClose={() => {
            setShowModal(false);
            setEditingConfig(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default OTApprovalConfigList;